import React, { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, HStack, Heading, Pressable, Text, VStack } from "native-base";
import { Helmet } from "react-helmet-async";
import { MdArrowRight } from "react-icons/md";
import { Link } from "react-router-dom";
import styles from "./styles.module.css";
import colors from "../../../../constants/colors";
import routes from "../../../../routes";
import Cell from "../../../../components/cell";
import { StateContext, StatusContext } from "../../../../contexts";
import FilePicker from "../../../../components/filePicker";
import API, { getFileUrl } from "../../../../api";
import { allowExit, preventExit } from "../../../../utils";

function isFile(state = {}, file = "") {
  return Boolean(state?.user?.agency?.documents[file]);
}

function FileUrl(props) {
  const { url, label } = props;

  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <Text underline color={colors.secondary}>
        {label}
      </Text>
    </a>
  );
}

function Document(props) {
  const { label, name, fileExists, file, onSelect, isUploading, onDelete } =
    props;

  return (
    <tr>
      <Cell>{typeof label === "string" ? <Text>{label}</Text> : label}</Cell>
      <Cell>
        {fileExists ? (
          <FileUrl url={getFileUrl(file.url)} label={file.name} />
        ) : (
          "N/A"
        )}
      </Cell>
      <Cell>
        <HStack space={3}>
          <FilePicker
            onSelect={([file]) => onSelect(file, name)}
            disabled={isUploading}>
            <Box
              backgroundColor={colors.primary}
              borderRadius={5}
              paddingY={1}
              paddingX={5}>
              <Text color={colors.white}>
                {fileExists ? "Volver a cargar" : "Cargar"}
              </Text>
            </Box>
          </FilePicker>
          {fileExists && (
            <Pressable
              borderWidth={1}
              borderColor={colors.primary}
              borderRadius={5}
              paddingY={1}
              paddingX={5}
              onPress={() => onDelete(name)}
              disabled={isUploading}>
              <Text color={colors.primary}>Eliminar</Text>
            </Pressable>
          )}
        </HStack>
      </Cell>
    </tr>
  );
}

function AgencyProfileDocumentsPage() {
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (isUploading) {
      preventExit();
    }
    return () => {
      allowExit();
    };
  }, [isUploading]);

  const onSelectDocument = useCallback(
    async (file, name) => {
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append("files", file);
        const {
          data: [{ id }],
        } = await API(state.jwt).post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        await API(state.jwt).put(
          `/agency-documents/${state.user.agency.documents.id}`,
          {
            data: { [name]: id },
          },
        );
        await state.refreshUser();
      } catch (error) {
        status.updateErrorMessage(error);
      }
      setIsUploading(false);
    },
    [state, status],
  );

  const onDeleteDocument = useCallback(
    async (name) => {
      setIsUploading(true);
      try {
        await API(state.jwt).put(
          `/agency-documents/${state.user.agency.documents.id}`,
          {
            data: { [name]: null },
          },
        );
        await state.refreshUser();
      } catch (error) {
        status.updateErrorMessage(error);
      }
      setIsUploading(false);
    },
    [state, status],
  );

  return (
    <VStack paddingX={60}>
      <Helmet>
        <title>Documentos</title>
      </Helmet>
      <HStack paddingY={8}>
        <Heading color={colors.secondary} fontSize="2xl">
          Mi perfil
        </Heading>
      </HStack>
      <HStack space={2} marginBottom={5}>
        <Link to={routes.agencyProfile}>
          <Heading fontSize="lg">Mi perfil</Heading>
        </Link>
        <MdArrowRight color={colors.secondary} size={25} />
        <Heading color={colors.secondary} fontSize="lg">
          Documentos
        </Heading>
      </HStack>
      <VStack backgroundColor="white" padding={10}>
        <table className={styles.table}>
          <thead>
            <tr>
              <Cell header>Documento</Cell>
              <Cell header>Link</Cell>
              <Cell header>Opciones</Cell>
            </tr>
          </thead>
          <tbody>
            <Document
              label="Constancia de Situación Fiscal"
              name="fiscalSituation"
              fileExists={isFile(state, "fiscalSituation")}
              file={state.user.agency.documents.fiscalSituation}
              onSelect={onSelectDocument}
              onDelete={onDeleteDocument}
              isUploading={isUploading}
            />
            <Document
              label="Verificación de Domicilio"
              name="domicile"
              fileExists={isFile(state, "domicile")}
              file={state.user.agency.documents.domicile}
              onSelect={onSelectDocument}
              onDelete={onDeleteDocument}
              isUploading={isUploading}
            />
            <Document
              label={
                <Text>
                  <Text bold>Identificación Oficial</Text>: INE,
                  <br />
                  Pasaporte, Cédula profesional
                </Text>
              }
              name="officialIdentification"
              fileExists={isFile(state, "officialIdentification")}
              file={state.user.agency.documents.officialIdentification}
              onSelect={onSelectDocument}
              onDelete={onDeleteDocument}
              isUploading={isUploading}
            />
            <Document
              label="RNT"
              name="rnt"
              fileExists={isFile(state, "rnt")}
              file={state.user.agency.documents.rnt}
              onSelect={onSelectDocument}
              onDelete={onDeleteDocument}
              isUploading={isUploading}
            />
            <Document
              label="Acta constitutiva"
              name="constitutiveAct"
              fileExists={isFile(state, "constitutiveAct")}
              file={state.user.agency.documents.constitutiveAct}
              onSelect={onSelectDocument}
              onDelete={onDeleteDocument}
              isUploading={isUploading}
            />
            <Document
              label="Poder notarial"
              name="notarialPower"
              fileExists={isFile(state, "notarialPower")}
              file={state.user.agency.documents.notarialPower}
              onSelect={onSelectDocument}
              onDelete={onDeleteDocument}
              isUploading={isUploading}
            />
            <Document
              label="Carta de autorización"
              name="authorizationLetter"
              fileExists={isFile(state, "authorizationLetter")}
              file={state.user.agency.documents.authorizationLetter}
              onSelect={onSelectDocument}
              onDelete={onDeleteDocument}
              isUploading={isUploading}
            />
          </tbody>
        </table>
      </VStack>
    </VStack>
  );
}

FileUrl.propTypes = {
  url: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

Document.propTypes = {
  label: PropTypes.any.isRequired,
  name: PropTypes.string.isRequired,
  fileExists: PropTypes.bool.isRequired,
  file: PropTypes.any,
  onSelect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
};

export default AgencyProfileDocumentsPage;
